import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Optional, Output, QueryList, SimpleChanges, TemplateRef, ViewChildren } from '@angular/core';
import { BaseTableDirective } from 'src/app/shared/directives/base-table.directive';
import { Store } from '@ngxs/store';
import { GenericTableColumn } from '../../interfaces/genetic-table-column';
import { FieldType } from '../../interfaces/field-type.enum';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { RowType } from '../../interfaces/raw-type.enum';
import { ILocationChosenEvent } from '../../interfaces/location-chosen-event.interface';
import { IInventoryLocation } from '../../interfaces/inventory-location.interface';
import { GetInventoryOnLocation } from '../../states/inventory/inventory.actions';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { InventoryState } from '../../states/inventory/inventory.state';
import { InventoryLocationsService } from '../../services/inventory-locations.service';
import { ActivityTypesEnum } from '../../interfaces/ActivityTypesEnum';
import { InputComponent } from '../input/input.component';
import { NUMBER_MIN_VALUE } from '../../constants/constants';
import Decimal from 'decimal.js';

@UntilDestroy()
@Component({
  selector: 'app-generic-write-table',
  templateUrl: './generic-write-table.component.html',
  styleUrls: ['./generic-write-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GenericWriteTableComponent<T> extends BaseTableDirective implements OnInit {
  @Input() formArray!: FormArray;
  @Input() columns: GenericTableColumn<T>[] = [];
  @Input() loading: boolean;
  @Input() useDefaultLocationChangeHandler: boolean;
  @Input() adNdcClasses: string;
  @Input() addItemWording: string = '+ Add new NDC';
  @Input() canAddNewItem: boolean = true;
  @Input() addNewItemAlwaysEnabled: boolean;
  @Input() activityType: ActivityTypesEnum = ActivityTypesEnum.Dispensed;
  @Input() tableMinHeight = '70px';
  @Input() excludeLocations: string[] = [];
  @Input() isScrollable: boolean = true;
  @Input() template: TemplateRef<any>;
  
  @Output() addNdc: EventEmitter<void> = new EventEmitter();
  @Output() locationChosen: EventEmitter<ILocationChosenEvent> = new EventEmitter();

  @Output() onNumberInputKeyupEnter: EventEmitter<void> = new EventEmitter();
  @Output() onLastNumberInputKeyupEnter: EventEmitter<void> = new EventEmitter();
  @Output() onNumberInputKeydownTab: EventEmitter<void> = new EventEmitter();
  @Output() onLastInputKeydownTab: EventEmitter<void> = new EventEmitter();
  @Output() rowDeleted: EventEmitter<any> = new EventEmitter();

  FieldType = FieldType;
  RowType = RowType;

  tableColumns = {};

  @ViewChildren('appInput') appInputs: QueryList<InputComponent>;

  /**
   *
   */
  constructor(protected store: Store, private cd: ChangeDetectorRef, @Optional() private inventoryLocationsService: InventoryLocationsService,
  private fb: FormBuilder) {
    super(store);
  }

  ngOnInit(): void {
    //propagate tableColumns for sorting to be able to move the arrow up and down
    if (this.columns) {
      this.tableColumns = this.columns
      .filter(column => column.sortable)
      .map(column => ({[column.field]: column.sortAsc}));
    }
  }

  getLocationsToExcludeWhichAlreadySelectedForTheDropdown(rowN: number, locationFieldName: string): string[] {
   const raw = (this.formArray.getRawValue())[rowN];

  const alreadySelected = (this.formArray.getRawValue())?.
    filter(x => x[locationFieldName] !== raw[locationFieldName] && (x?.drugId || x?.drugID) === (raw?.drugId || raw?.drugID))?.
    map(x => x[locationFieldName]) || [];
    return alreadySelected.concat(this.excludeLocations);
  }

  removeNote(control: FormControl): void {
    control.setValue(null);
  }

  saveAddedNote(text: string, control: FormControl): void {
    control.setValue(text);
  }

  //TODO: bug if remove at 0, app-input will not update its value
  deleteRow(i: number): void {
    const toRemove = {...this.formArray.at(i).value};
    this.formArray.removeAt(i);
    this.rowDeleted.next(toRemove);
    this.formArray.updateValueAndValidity();
  }

  handleChosenLocation(location: IInventoryLocation,  rowN: number, columnDescriptor: GenericTableColumn<T>): void {
    if (!this.useDefaultLocationChangeHandler) {
      this.locationChosen.emit({location, rowN});
      return;
    }
    const drugID = this.formArray.getRawValue()[rowN]?.[columnDescriptor.locationRow.drugIDField]
    this.store.dispatch(new GetInventoryOnLocation(location.id, drugID))
    .pipe(untilDestroyed(this))
    .subscribe(() => {
      const currentInventoryOnLocation = this.store.selectSnapshot(InventoryState.currentInventoryForNdcOnLocation);
      this.updateOnHandValueForNdc(rowN, currentInventoryOnLocation.onHand, columnDescriptor);
      this.locationChosen.emit({location, rowN});
    });
  }

  private updateOnHandValueForNdc(rowN: number, onHand: number, columnDescriptor: GenericTableColumn<T>): void {
    const raw = this.formArray.controls[rowN];
    raw.get(columnDescriptor.locationRow.startingField.toString()).setValue(onHand);
    if (this.activityType !== ActivityTypesEnum.Received) {
      raw.get(columnDescriptor.locationRow.countField.toString()).setValidators([Validators.required, Validators.max(onHand), Validators.min(NUMBER_MIN_VALUE)]);
      raw.get(columnDescriptor.locationRow.onHandField.toString()).setValue(new Decimal(+onHand).sub(new Decimal(+raw.get('count').value)).toNumber());
    } else {
      raw.get(columnDescriptor.locationRow.countField.toString()).setValidators([Validators.required, Validators.min(NUMBER_MIN_VALUE)]);
      raw.get(columnDescriptor.locationRow.onHandField.toString()).setValue(new Decimal(+onHand).add(new Decimal(+raw.get('count').value).toNumber()));
    }
    
    raw.get(columnDescriptor.locationRow.countField.toString()).updateValueAndValidity();

    this.cd.detectChanges();
  } 

  sortByColumn(column: GenericTableColumn<T>): void {
    let formArrayVal = this.formArray.getRawValue();
    formArrayVal = this.sort(formArrayVal, column.field.toString());
    this.formArray.clear();
    formArrayVal.forEach(value => this.formArray.push(this.fb.control(value)));
  }

  trackByIndex(index: number, obj: any): any {
    return index;
  }

  markForCheck(): void {
    this.cd.markForCheck();
  }

  handleNumberInputEnter(index: number): void {
    if (index == this.formArray.controls.length - 1) {
      event.preventDefault();
      this.onLastNumberInputKeyupEnter.next();
    }
    this.onNumberInputKeyupEnter.next();
  }

  handleNumberInputKeydownTab(index: number): void {
    if (index == this.formArray.controls.length - 1) {
      event.preventDefault();
      this.onLastInputKeydownTab.next();
    }
    this.onNumberInputKeydownTab.next();
  }

  focusInput(index: number): void {
    this.appInputs.toArray()[index]?.focusInput();
  }

}
